import { Component, OnInit, OnDestroy } from '@angular/core';
import { SharedDataService } from '../../../services/shared-data.service';
import { CpMarketplaceApiService } from '../../../services/cp-marketplace-api.service';
import { CpAuthenticationService } from '../../../services/cp-authentication.service';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { interval, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { LOGIN_MESSAGES } from 'src/app/messages';
import { UserData, InstrumentEnum, OTP_RESEND_INTERVAL } from 'appRootPath/shared/global-meta-data';
import { Router } from '@angular/router';
import { CompanyDetailsMini, LoginResponse } from 'appRootPath/models/commercial-papers.model';
import { EncryptionService } from 'appRootPath/services/cp-encryption.service';

@Component({
  selector: 'app-main-login-form',
  templateUrl: './main-login-form.component.html',
  styleUrls: ['./main-login-form.component.scss']
})

/**
 * This is the main login form component. Main word is used to first component in the heirarchy of the login forms.
 */
export class MainLoginFormComponent implements OnInit, OnDestroy {
  getOtpForm: FormGroup;
  loginWithOtpForm: FormGroup;
  formGetOtpSubmittedInvalid = false;
  formLoginSubmittedInvalid = false;
  maskingValue = '';
  actualPassword = '';
  stateMap = {
    GET_OTP: 'get_otp',
    LOGIN: 'login_with_otp'
  };
  showCompanySelection = false;
  companiesList: CompanyDetailsMini[];

  currentState = this.stateMap.GET_OTP;

  otpCounterSecondsMax = OTP_RESEND_INTERVAL;
  otpCounterSecondsRemaining = OTP_RESEND_INTERVAL;
  subscription: Subscription;
  publicKey;

  constructor(
    private sharedData: SharedDataService,
    private apiService: CpMarketplaceApiService,
    private authenticationService: CpAuthenticationService,
    private encryptionService: EncryptionService,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private router: Router
  ) { }

  ngOnInit() {
    this.sharedData.clearSessionStorage();
    this.startCounter();

    this.initOTPForm();
    this.initLoginForm();

    this.setFormSubscriptions();
    this.sharedData.getEncrytedPublicKey();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
  
  maskPassword(event: any) {
    const input = event.target;
    const lastChar = input.value.slice(-1);
    if (input.value.length > this.maskingValue.length) {
      this.actualPassword += lastChar; // Store actual password
    } else {
      this.actualPassword = this.actualPassword.slice(0, -1); // Handle backspace
    }
    this.maskingValue = "•".repeat(this.actualPassword.length); // Show masked value
    input.value = this.maskingValue;    
  }

  /**
   * Initialises OTP Form.
   */
  initOTPForm() {
    this.getOtpForm = this.fb.group({
      username: ['', Validators.compose([Validators.email, Validators.required])]
    });
  }

  /**
   * Initialises Login Form.
   */
  initLoginForm() {
    this.loginWithOtpForm = this.fb.group({
      username: ['', Validators.compose([Validators.email, Validators.required])],
      otp: ['', Validators.required],
      password: ['', Validators.required],
      temp_session_id: ['', Validators.required]
    });
  }

  /**
   * Handles starting of the counter for OTP.
   */
  startCounter() {
    this.otpCounterSecondsRemaining = OTP_RESEND_INTERVAL;

    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = interval(1000).pipe(
      take(this.otpCounterSecondsMax)
    ).subscribe((val) => {
      this.otpCounterSecondsRemaining -= 1;
    });
  }

  setFormSubscriptions() {
    this.loginWithOtpForm.get('username').valueChanges.subscribe((loginUsername) => {
      if (loginUsername && loginUsername !== this.getOtpForm.get('username').value) {
        this.currentState = this.stateMap.GET_OTP;
        this.getOtpForm.reset();
        this.getOtpForm.get('username').setValue(loginUsername);
      }
    });
  }

  submitEmailForOtp(): void {
    if (this.getOtpForm.invalid) {
      this.formGetOtpSubmittedInvalid = true;
      const validationErrors = [];
      if (this.getOtpForm.controls['username'].errors &&
        this.getOtpForm.controls['username'].errors.email) {
        validationErrors.push('Please enter the correct email address.');
      } else if (this.getOtpForm.controls['username'].errors &&
        this.getOtpForm.controls['username'].errors.required) {
        validationErrors.push('User\'s Email address is required.');
      }
      if (validationErrors.length) {
        this.toastr.warning(validationErrors.join());
      }
    } else {
      this.formGetOtpSubmittedInvalid = false;
      const getOtpPostData = this.getOtpForm.value;
      this.apiService.getOtp(getOtpPostData).subscribe((successResp: LoginResponse) => {
        this.toastr.success(LOGIN_MESSAGES.OTP_SENT_SUCCESS);
        this.currentState = this.stateMap.LOGIN;
        this.startCounter();
        this.loginWithOtpForm.reset();
        this.publicKey = this.encryptionService.getPublicKey(successResp.credentials_public_key);
        this.loginWithOtpForm.get('temp_session_id').setValue(successResp.temp_session_id);
        this.loginWithOtpForm.get('username').setValue(getOtpPostData.username);
      }, (errorResp) => {
        setTimeout(() => {
          this.toastr.success(LOGIN_MESSAGES.OTP_SENT_ERROR);
        }, 200);
        this.currentState = this.stateMap.GET_OTP;
      }
      );
    }
  }

  /**
   * Handles login with OTP.
   */
  loginWithOtp() {
    if (this.loginWithOtpForm.invalid) {
      this.toastr.warning(LOGIN_MESSAGES.OTP_FORM_INVALID);
      this.formLoginSubmittedInvalid = true;
    } else {
      this.formLoginSubmittedInvalid = false;
      const loginWithOtpData = this.parseLoginAPIData(this.loginWithOtpForm.value);

      this.apiService.login(loginWithOtpData).subscribe((loginSuccessResp: UserData) => {
        this.handleProcessesPostLogin(loginSuccessResp);
      }, (errorResp) => {
        this.handleLoginErrorResponse(errorResp);
      });
    }
  }

  /**
   * Returns the parsed data for the Login API.
   * @param loginFormValue - Value of the login form.
   */
  parseLoginAPIData(loginFormValue) {
    const otpStr = loginFormValue.otp.toString();
    loginFormValue.password = this.actualPassword;
      return {
      username: this.encryptionService.getCipherText(loginFormValue.username, this.publicKey),
      otp: this.encryptionService.getCipherText(otpStr, this.publicKey),
      temp_session_id: loginFormValue.temp_session_id,
      password: this.encryptionService.getCipherText(loginFormValue.password, this.publicKey)
    };
  }

  /**
   * Handles the processing of login response post successful login.
   * @param loginResponse - Response of the login API.
   */
  handleProcessesPostLogin(loginResponse: UserData) {
    if (!this.isCPInstrumentAllowed(loginResponse)) {
      this.currentState = this.stateMap.GET_OTP;
      this.resetAllForms();
      this.toastr.error(LOGIN_MESSAGES.SERVICE_FORBIDDEN);
    } else if (loginResponse.token) {
      this.authenticationService.storeLoginData(loginResponse);
      this.handleHolidaysLogic();
    }
  }

  handleLoginErrorResponse(errorResponse) {
    if (errorResponse.error && errorResponse.status === 300 && errorResponse.error.companies &&
      errorResponse.error.companies.length) {

      this.sharedData.setCurrentUserToken(errorResponse.error.token, errorResponse.error.user_profile_name);
      this.handleMultipleCompanies(errorResponse.error.companies);

    } else {
      this.sharedData.setLoginState(false);

      setTimeout(() => {
        this.toastr.error(errorResponse.error);
      }, 200);

      this.resetMultipleCompaniesLogic();
      this.handleLoginErrorsByCode(errorResponse.status);
    }
  }

  /**
   * Resets the multiple companies logic.
   */
  resetMultipleCompaniesLogic() {
    this.showCompanySelection = false;
    this.companiesList = null;
  }

  /**
   * Handles the logic for user present in multiple companies.
   * @param companiesList - List of companies.
   */
  handleMultipleCompanies(companiesList: CompanyDetailsMini[]) {
    this.showCompanySelection = true;
    this.companiesList = companiesList;
  }

  /**
   * Handles the holidays logic. Fetches holidays list if the session storage does not contain
   * any holidays list. Else, updates the session storage with the new list.
   */
  handleHolidaysLogic() {
    const holidaysList = this.sharedData.getHolidaysFromLS();

    if (!(holidaysList && holidaysList.length)) {
      this.fetchHolidaysList();
    } else {
      // TODO: Updating of Holidays list may not be required since we are always clearing
      // the session storage when we are on login screen.
      this.sharedData.updateHolidays();
    }
  }

  /**
   * Fetches the list of holidays.
   */
  fetchHolidaysList() {
    this.apiService.getAllHolidays().subscribe(
      holidaysList => this.sharedData.setHolidaysToLS(holidaysList),
      error => this.sharedData.setHolidaysToLS(null)
    );
  }
  // Default case error message
  showErrorMessage() {
    this.toastr.warning('An unexpected error occurred. Please try again later.');
  }

  /**
   * Handles the logic to be done based on the error status codes.
   * @param errorStatus - Status code of the error response from backend.
   */
  handleLoginErrorsByCode(errorStatus: number) {
    switch (errorStatus) {
      // 401 for password OTP error
      case 401:
        // Do Nothing.
        // this.loginWithOtpForm.reset();
        break;

      // 403 for forbidden access
      case 403:
        this.handleForbiddenError();
        break;

      // 452 for password expired
      case 452:
        this.router.navigate(['login', 'forgot-password']);
        break;

      // 453 for account deactivated
      case 453:
        this.handleForbiddenError();
        this.getOtpForm.reset();
        break;
      //default Case

      default: 
        this.showErrorMessage();
        break;
    }
  }

  /**
   * Resets all the forms in the component.
   */
  resetAllForms() {
    this.getOtpForm.reset();
    this.loginWithOtpForm.reset();
  }

  handleForbiddenError() {
    this.currentState = this.stateMap.GET_OTP;
    this.getOtpForm.reset();
    this.getOtpForm.get('username').setValue(this.loginWithOtpForm.get('username').value);
    this.loginWithOtpForm.reset();
  }

  resendOtp() {
    const resendOtpData = {
      temp_session_id: this.loginWithOtpForm.get('temp_session_id').value
    };

    this.apiService.resendOtp(resendOtpData).subscribe((successResp) => {
      this.toastr.success(LOGIN_MESSAGES.OTP_SENT_SUCCESS);
      this.startCounter();
    }, (errorResp) => {
      setTimeout(() => {
        this.toastr.success(LOGIN_MESSAGES.OTP_SENT_ERROR);
      }, 200);
      this.startCounter();
    });
  }

  isCPInstrumentAllowed(loginUserData: UserData) {
    let isCPAllowed = false;
    if (loginUserData.party_profile) {
      loginUserData.party_profile.company.divisions.forEach((division) => {
        division.instrument.forEach((instr) => {
          if (instr.instrument_type === InstrumentEnum.COMMERCIAL_PAPER) {
            isCPAllowed = true;
          }
        });
      });
    }

    if (loginUserData.corporate_profile) {
      loginUserData.corporate_profile.CompanyDivision.forEach((division) => {
        division.AssociatedInstrument.forEach((instr) => {
          if (instr.instrument_type === InstrumentEnum.COMMERCIAL_PAPER) {
            isCPAllowed = true;
          }
        });
      });
    }

    return isCPAllowed;
  }

  editUsername() {
    this.currentState = this.stateMap.GET_OTP;
    this.getOtpForm.reset();
    this.getOtpForm.get('username').setValue(this.loginWithOtpForm.get('username').value);
    this.loginWithOtpForm.reset();
  }

}
